import React, { useState, useMemo } from 'react';
import { Button } from 'components/ui/button';
import SimpleHeader from 'components/SimpleHeader';
import { ShoppingCart, Check, Plus, Minus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PACKAGE_CATEGORIES } from '../data/packages';

// --- SABİT VERİLER ---
const MASA_KARTI_TASARIMLARI = [
    {
        id: 'klasik-masa',
        name: 'Klasik Beyaz',
        designImageUrl: '/baski/masa-karti4-tasarim-beyaz.png',
        productImageUrl: '/baski/masa-karti4-gorunum-beyaz.png'
    },
    {
        id: 'modern-masa-pembe',
        name: 'Modern Pembe',
        designImageUrl: '/baski/masa-karti1-tasarim-pembe.png',
        productImageUrl: '/baski/masa-karti1-gorunum-pembe.png'
    },
    {
        id: 'modern-masa-mavi',
        name: 'Modern Mavi',
        designImageUrl: '/baski/masa-karti2-tasarim-mavi.jpeg',
        productImageUrl: '/baski/masa-karti2-gorunum-mavi.png'
    },
    {
        id: 'modern-masa-nane',
        name: 'Nane Yeşili',
        designImageUrl: '/baski/masa-karti3-tasarim-nane.jpeg',
        productImageUrl: '/baski/masa-karti3-gorunum-nane.png'
    },
    {
        id: 'retro-masa',
        name: 'Retro',
        designImageUrl: '/baski/masa-karti-5.png',
        productImageUrl: '/baski/masa-karti-5.png'
    },
    {
        id: 'fotograf-masa',
        name: 'Fotoğraflı',
        designImageUrl: '/baski/masa-karti-6.png',
        productImageUrl: '/baski/masa-karti-6.png'
    },
];

const EL_KARTI_TASARIMLARI = [
    {
        id: 'sade-el',
        name: 'Sade Arka Plan',
        designImageUrl: '/baski/el-karti1-tasarim.webp',
        productImageUrl: '/baski/el-karti1-gorunum.png'
    },
];

const PANO_TASARIMLARI = [
    {
        id: 'standart-pano',
        name: 'Standart Pano',
        designImageUrl: '/baski/pano.webp',
        productImageUrl: '/baski/pano.webp'
    },
];

const TEK_YUZ_MASA_KARTI_TASARIMLARI = [
    {
        id: 'standart-tek-yuz',
        name: 'Standart Tek Yüz',
        designImageUrl: '/baski/tek-yuz-masa-karti.png',
        productImageUrl: '/baski/tek-yuz-masa-karti.png'
    }
];

const PRODUCTS = [
    {
        id: 'masa-karti',
        title: 'Masa Kartı',
        description: 'Her masa için stand üzerinde QR kartı.',
        price: 12.00,
        maxQty: 300, // Ek alınabilecek maksimum adet
        designs: MASA_KARTI_TASARIMLARI,
        isPortrait: false // Yatay
    },
    {
        id: 'tek-yuz-masa-karti',
        title: 'Tek Yüzlü Masa Kartı',
        description: 'Bütçe dostu, tek yüzlü QR masa kartı.',
        price: 7.50,
        maxQty: 300, // Ek alınabilecek maksimum adet
        designs: TEK_YUZ_MASA_KARTI_TASARIMLARI,
        isPortrait: false // Yatay
    },
    {
        id: 'el-karti',
        title: 'El Kartı',
        description: 'Misafirlerin eline verilecek 300gr orijinal baskı kağıdı.',
        price: 5.00,
        maxQty: 500, // Ek alınabilecek maksimum adet
        designs: EL_KARTI_TASARIMLARI,
        isPortrait: true // Dikey
    },
    {
        id: 'karsilama-panosu',
        title: 'Karşılama Panosu',
        description: 'Düğün girişinde misafirlerinizi karşılayacak dev QR özellikli pano.',
        price: 1000.00,
        maxQty: 10,
        designs: PANO_TASARIMLARI,
        isPortrait: true // Dikey Poster
    }
];

type QuantitiesState = { [key: string]: number };

export const getCustomMasaKartiUnitPrice = (qty: number) => {
    return 12;
};

export default function BaskiPage() {
    const navigate = useNavigate();

    // Seçili Tasarımlar State'i
    const [selectedDesigns, setSelectedDesigns] = useState<{ [key: string]: string }>({
        'masa-karti': MASA_KARTI_TASARIMLARI[0].id,
        'tek-yuz-masa-karti': TEK_YUZ_MASA_KARTI_TASARIMLARI[0].id,
        'el-karti': EL_KARTI_TASARIMLARI[0].id,
        'karsilama-panosu': PANO_TASARIMLARI[0].id
    });

    const packageSelected = useMemo(() => localStorage.getItem('anIkareSelectedPackage') !== null, []);
    
    const isCustomPackage = useMemo(() => {
        try {
            const storedData = localStorage.getItem('anIkareSelectedPackage');
            if (storedData) {
                const pkg = JSON.parse(storedData);
                return pkg.id === 'dugun-ozel';
            }
        } catch (e) {}
        return false;
    }, []);

    // Ücretsiz hakları çek
    const { FREE_MASA_KARTI_QTY, FREE_TEK_YUZ_MASA_QTY, FREE_EL_KARTI_QTY, FREE_BOARD_QTY } = useMemo(() => {
        let freeMasa = 0, freeTekYuzMasa = 0, freeEl = 0, freeBoard = 0;
        try {
            const storedData = localStorage.getItem('anIkareSelectedPackage');
            if (storedData) {
                const parsedPkg = JSON.parse(storedData);
                // En güncel veriyi almak için packages.ts'ten id ile bul
                const allPackages = PACKAGE_CATEGORIES.flatMap(c => c.packages);
                const pkg = allPackages.find(p => p.id === parsedPkg.id) || parsedPkg;
                
                if (pkg.id && pkg.id.startsWith('ekonomik')) {
                    freeTekYuzMasa = pkg.freeTableCardQty || 0;
                    freeMasa = 0;
                } else {
                    freeMasa = pkg.freeTableCardQty || 0;
                    freeTekYuzMasa = 0;
                }
                freeEl = pkg.freeHandCardQty || 0;
                freeBoard = pkg.freeBoardQty || 0;
            }
        } catch (e) { console.error(e); }
        return { 
            FREE_MASA_KARTI_QTY: freeMasa, 
            FREE_TEK_YUZ_MASA_QTY: freeTekYuzMasa, 
            FREE_EL_KARTI_QTY: freeEl, 
            FREE_BOARD_QTY: freeBoard 
        };
    }, []);

    // State artık sadece "EKSTRA" (Extra) adetleri tutuyor. Varsayılan 0.
    const [extraQuantities, setExtraQuantities] = useState<QuantitiesState>({
        'el-karti': 0,
        'masa-karti': 0,
        'tek-yuz-masa-karti': 0,
        'karsilama-panosu': 0
    });

    const handleExtraQuantityChange = (productId: string, val: number) => {
        const product = PRODUCTS.find(p => p.id === productId);
        if (!product) return;
        
        // Negative kontrol
        if (val < 0) val = 0;
        
        if (val > product.maxQty) val = product.maxQty;
        setExtraQuantities(prev => ({ ...prev, [productId]: val }));
    };

    const handleDesignSelect = (productId: string, designId: string) => {
        setSelectedDesigns(prev => ({ ...prev, [productId]: designId }));
    };

    // Fiyat Hesaplama (Sadece Ek Adetler Üzerinden)
    const totalPrice = useMemo(() => {
        return PRODUCTS.reduce((total, product) => {
            const extraQty = extraQuantities[product.id] || 0;
            if (isCustomPackage && product.id === 'masa-karti' && extraQty > 0) {
                const unitPrice = getCustomMasaKartiUnitPrice(extraQty);
                return total + Math.round(extraQty * unitPrice);
            }
            return total + (extraQty * product.price);
        }, 0);
    }, [extraQuantities, isCustomPackage]);

    const canContinue = useMemo(() => (packageSelected || totalPrice > 0), [packageSelected, totalPrice]);

    const handleContinue = () => {
        // Kaydederken "Toplam Adet" (Ücretsiz + Ek) mantığına dönüştürüp kaydediyoruz
        // Böylece sepet sayfası mantığı bozulmaz.
        const finalQuantitiesToSave = {
            'masa-karti': (extraQuantities['masa-karti'] || 0) + FREE_MASA_KARTI_QTY,
            'tek-yuz-masa-karti': (extraQuantities['tek-yuz-masa-karti'] || 0) + FREE_TEK_YUZ_MASA_QTY,
            'el-karti': (extraQuantities['el-karti'] || 0) + FREE_EL_KARTI_QTY,
            'karsilama-panosu': (extraQuantities['karsilama-panosu'] || 0) + FREE_BOARD_QTY
        };

        localStorage.setItem('orderQuantities', JSON.stringify(finalQuantitiesToSave));

        const masaDesignObj = MASA_KARTI_TASARIMLARI.find(d => d.id === selectedDesigns['masa-karti']);
        const tekYuzDesignObj = TEK_YUZ_MASA_KARTI_TASARIMLARI.find(d => d.id === selectedDesigns['tek-yuz-masa-karti']);
        const elDesignObj = EL_KARTI_TASARIMLARI.find(d => d.id === selectedDesigns['el-karti']);
        const panoDesignObj = PANO_TASARIMLARI.find(d => d.id === selectedDesigns['karsilama-panosu']);

        localStorage.setItem('selectedPrintDesigns', JSON.stringify({
            masaKarti: { id: selectedDesigns['masa-karti'], name: masaDesignObj?.name },
            tekYuzMasaKarti: { id: selectedDesigns['tek-yuz-masa-karti'], name: tekYuzDesignObj?.name },
            elKarti: { id: selectedDesigns['el-karti'], name: elDesignObj?.name },
            karsilamaPanosu: { id: selectedDesigns['karsilama-panosu'], name: panoDesignObj?.name },
        }));

        navigate('/adres-bilgileri');
    };

    return (
        <div className="bg-rose-50/50 min-h-screen font-sans text-gray-800">
            <SimpleHeader />

            <main className="mx-auto max-w-6xl px-4 py-8 pb-32">
                <div className="text-center mb-10">
                    <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight">Kart Tasarımını Seç</h1>
                    <p className="mt-2 text-lg text-gray-500">
                        Paketindeki haklara ek olarak istediğin adeti belirleyebilirsin.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                    {PRODUCTS.map((product) => {
                        const currentDesignId = selectedDesigns[product.id];
                        const currentDesign = product.designs.find(d => d.id === currentDesignId) || product.designs[0];

                        // Hesaplamalar
                        const extraQty = extraQuantities[product.id] || 0;
                        const freeQty = 
                            product.id === 'masa-karti' 
                                ? FREE_MASA_KARTI_QTY 
                                : product.id === 'tek-yuz-masa-karti' 
                                    ? FREE_TEK_YUZ_MASA_QTY 
                                    : product.id === 'el-karti' 
                                        ? FREE_EL_KARTI_QTY 
                                        : FREE_BOARD_QTY;
                        const totalQty = freeQty + extraQty; // Gösterilecek Toplam
                        const extraPrice = (isCustomPackage && product.id === 'masa-karti' && extraQty > 0)
                            ? Math.round(extraQty * getCustomMasaKartiUnitPrice(extraQty))
                            : extraQty * product.price;

                        return (
                            <div key={product.id} className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden flex flex-col h-full transition-all hover:shadow-xl">

                                {/* 1. GÖRSEL ALANI - *** DEĞİŞİKLİK BURADA (El Kartı için boyutlandırma güncellendi) *** */}
                                <div className="relative w-full bg-gray-50 border-b border-gray-100 group">
                                    <div className={`relative mx-auto overflow-hidden
                                        ${product.isPortrait && product.id === 'karsilama-panosu' 
                                            ? 'w-[200px] aspect-[3/4] sm:w-[250px] lg:w-[220px] p-2 bg-neutral-100 rounded'
                                            : product.isPortrait
                                            // El Kartı için daha belirgin dikey boyutlandırma ve daraltma
                                            ? 'w-[250px] aspect-[4/5] sm:w-[300px] lg:w-[250px] p-2'
                                            // Masa Kartı için genişletilmiş ve yatay oran
                                            : 'w-full aspect-video max-w-lg lg:max-w-md p-4'
                                        }
                                    `}>
                                        <img
                                            src={currentDesign.productImageUrl}
                                            alt={currentDesign.name}
                                            className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
                                        />
                                    </div>
                                    <div className="absolute top-3 left-3 bg-white/90 backdrop-blur text-xs font-bold px-2 py-1 rounded shadow-sm text-gray-600 border border-gray-200">
                                        {currentDesign.name}
                                    </div>
                                </div>

                                {/* 2. THUMBNAIL SEÇİM ALANI */}
                                {product.designs.length > 1 && (
                                    <div className="px-4 py-3 bg-white border-b border-gray-100 overflow-x-auto">
                                        <div className="flex gap-3 justify-center">
                                            {product.designs.map((design) => {
                                                const isSelected = currentDesignId === design.id;
                                                return (
                                                    <button
                                                        key={design.id}
                                                        onClick={() => handleDesignSelect(product.id, design.id)}
                                                        className={`
                                                            group relative flex-shrink-0 cursor-pointer 
                                                            w-16 h-12 rounded border transition-all
                                                            ${isSelected
                                                                ? 'border-[#e45078] ring-1 ring-[#e45078] opacity-100'
                                                                : 'border-gray-200 hover:border-rose-300 opacity-70 hover:opacity-100'}
                                                        `}
                                                        title={design.name}
                                                    >
                                                        <img
                                                            src={design.designImageUrl}
                                                            alt={design.name}
                                                            className="w-full h-full object-cover rounded-[3px]"
                                                        />
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* 3. DETAYLAR VE ADET GİRİŞİ */}
                                <div className="p-5 flex flex-col">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h3 className="text-lg font-bold text-gray-800">{product.title}</h3>
                                            <p className="text-xs text-gray-500">{product.description}</p>
                                        </div>
                                        <div className="text-right">
                                            <>
                                                <span className="block text-lg font-bold text-gray-900">₺{product.price.toLocaleString('tr-TR')}</span>
                                                <span className="text-[10px] text-gray-400">/ ek adet</span>
                                            </>
                                        </div>
                                    </div>

                                    {/* Adet Kontrolü - Kullanıcı Sadece Ek Adeti Girer */}
                                    <div className="mt-3 bg-gray-50 rounded-lg p-3 border border-gray-200">

                                        {/* Bilgi Satırı */}
                                        <div className="flex justify-between items-center text-sm mb-2">
                                            <span className="text-gray-600">Paket Hakkı:</span>
                                            <span className="text-green-600 font-bold bg-green-100 px-2 py-0.5 rounded text-xs">
                                                {freeQty} Adet Ücretsiz
                                            </span>
                                        </div>
                                        


                                        {/* Input Alanı (Eski Kod Boyutlarında - h-10) */}
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="text-sm font-medium text-gray-700 w-20">Ek Adet:</span>
                                            <div className="flex items-center flex-1 h-10 border border-gray-300 rounded bg-white overflow-hidden">
                                                <button
                                                    onClick={() => handleExtraQuantityChange(product.id, extraQty - 1)}
                                                    className="w-10 h-full flex items-center justify-center bg-gray-50 hover:bg-gray-100 border-r text-gray-600 transition-colors"
                                                >
                                                    <Minus className="w-4 h-4" />
                                                </button>
                                                <input
                                                    type="number"
                                                    value={extraQty}
                                                    onChange={(e) => handleExtraQuantityChange(product.id, parseInt(e.target.value) || 0)}
                                                    onBlur={(e) => {
                                                        let val = parseInt(e.target.value) || 0;
                                                        if (isCustomPackage && product.id === 'masa-karti' && val > 0 && val < 10) {
                                                            handleExtraQuantityChange(product.id, 10);
                                                        }
                                                    }}
                                                    className="flex-1 h-full text-center font-bold text-gray-800 focus:outline-none"
                                                    placeholder="0"
                                                />
                                                <button
                                                    onClick={() => handleExtraQuantityChange(product.id, extraQty + 1)}
                                                    className="w-10 h-full flex items-center justify-center bg-gray-50 hover:bg-gray-100 border-l text-gray-600 transition-colors"
                                                >
                                                    <Plus className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Toplam Sonuç Satırı */}
                                        <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                                            <div className="text-sm text-gray-600">
                                                Toplam: <strong className="text-gray-900 text-base">{totalQty}</strong> adet
                                            </div>
                                            {extraQty > 0 && (
                                                <div className="text-[#e45078] font-bold text-sm">
                                                    + ₺{extraPrice.toLocaleString('tr-TR')}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Seçim Butonu Görünümlü Footer */}
                                    <div className="mt-4 w-full bg-[#111827] text-white py-2 rounded text-center text-sm font-medium">
                                        Seçildi: {currentDesign.name}
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </main>

            {/* ALT BAR */}
            <footer className="fixed bottom-0 w-full bg-white/95 backdrop-blur border-t border-rose-100 shadow-2xl z-50">
                <div className="mx-auto max-w-6xl px-4 py-3 flex justify-between items-center">
                    <div className="flex flex-col">
                        <span className="text-xs text-gray-500 font-medium uppercase">Ek Ödeme Tutarı</span>
                        <span className="text-2xl font-extrabold text-[#e45078]">₺{totalPrice.toLocaleString('tr-TR')}</span>
                    </div>

                    <Button
                        size="lg"
                        onClick={handleContinue}
                        disabled={!canContinue}
                        className={`px-8 font-bold transition-all
                            ${canContinue ? 'bg-[#e45078] hover:bg-rose-600 text-white shadow-lg shadow-rose-200' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}
                        `}
                    >
                        Devam Et
                    </Button>
                </div>
            </footer>
        </div>
    );
}